import { Flight } from "../models/flight";
import { Airport } from "../models/airport";
import { getDistance, stateForDistance } from "../utils/airport_distance";
import { flightSuggestions } from "@/utils/flight_suggestions";

export interface Guess {
  airport: Airport;
  type: "origin" | "destination";
  isCorrect: boolean;
}

export interface RoundState {
  score: number;
  hints: Record<string, string>;
  flight?: Flight;
  flightPath?: [lat: number, lon: number][];
  airports?: Airport[];
  originState: "not_set" | "success" | "error";
  destinationState: "not_set" | "success" | "error";
  originGuessDistance: number;
  destinationGuessDistance: number;
  originGuess: string;
  destinationGuess: string;
  airportStates: Record<
    string,
    {
      state: "origin" | "destination" | "default" | "cold" | "warm" | "hot";
      distance?: number;
    }
  >;
  lastGuess: Guess | null;
  airportSuggestions: string[];
  incorrectGuessCount: number;
}

export enum RoundActionType {
  SetFlight = "setFlight",
  SetFlightPath = "setFlightPath",
  SetAirports = "setAirports",
  RevealHint = "revealHint",
  ClearGuessState = "clearGuessState",
  SubmitGuess = "submitGuess",
  GiveUp = "giveUp",
}

export function roundReducer(
  state: RoundState,
  action: { type: RoundActionType; payload: any }
): RoundState {
  switch (action.type) {
    case RoundActionType.SetFlight:
      return {
        score: 1000,
        hints: {},
        flight: action.payload,
        airportStates: {},
        airports: state.airports,
        originState: "not_set",
        destinationState: "not_set",
        originGuessDistance: -1,
        destinationGuessDistance: -1,
        originGuess: "",
        destinationGuess: "",
        lastGuess: null,
        incorrectGuessCount: 0,
        airportSuggestions:
          (state.airports || []).length > 0
            ? flightSuggestions(action.payload, state.airports ?? [])
            : [],
      };
    case RoundActionType.SetFlightPath:
      return {
        ...state,
        flightPath: action.payload,
        // flight: {
        //   ...state.flight!,
        //   position: {
        //     ...state.flight!.position,
        //     lat: action.payload[0][0] ?? 0,
        //     lon: action.payload[0][1] ?? 0,
        //   },
        // },
      };
    case RoundActionType.SetAirports:
      return {
        ...state,
        airports: action.payload,
        airportSuggestions:
          state.airportSuggestions.length > 0
            ? state.airportSuggestions
            : flightSuggestions(state.flight!, action.payload),
      };
    case RoundActionType.RevealHint:
      let hint;

      switch (action.payload) {
        case "airline":
          hint = state.flight?.airline;
          break;
        case "altitude":
          hint = `${state.flight?.position.altitude.toLocaleString(
            navigator.language
          )} ft`;
          break;
        case "duration":
          hint = `${
            state.flight?.flight_duration === 0
              ? "<1"
              : state.flight?.flight_duration
          } hr${state.flight?.flight_duration === 1 ? "" : "s"}`;
          break;
        case "originTimezone":
          hint = state.flight?.origin_airport.timezone;
          break;
        case "destinationTimezone":
          hint = state.flight?.destination_airport.timezone;
          break;
      }

      return {
        ...state,
        score: state.score - 30,
        hints: { ...state.hints, [action.payload]: hint },
      };
    case RoundActionType.SubmitGuess:
      const airport =
        action.payload.type === "origin"
          ? state.flight?.origin_airport
          : state.flight?.destination_airport;

      if (
        airport?.iata.toLowerCase() ===
        action.payload.airport_guess.toLowerCase()
      ) {
        return {
          ...state,
          lastGuess: {
            airport: airport!,
            type: action.payload.type,
            isCorrect: true,
          },
          airportStates: {
            ...state.airportStates,
            [airport?.iata || ""]: {
              state: action.payload.type,
              distance: undefined,
            },
          },
          [action.payload.type === "origin"
            ? "originState"
            : "destinationState"]: "success",
          [action.payload.type === "origin"
            ? "originGuess"
            : "destinationGuess"]: action.payload.airport_guess,
        };
      }

      const guessedAirport = state.airports?.find(
        (airport) =>
          airport.iata.toLowerCase() ===
          action.payload.airport_guess.toLowerCase()
      );

      if (!guessedAirport) {
        return {
          ...state,
          [action.payload.type === "origin"
            ? "originGuess"
            : "destinationGuess"]: action.payload.airport_guess,
        };
      }

      const distance = getDistance(guessedAirport, airport!);
      return {
        ...state,
        lastGuess: {
          airport: guessedAirport,
          type: action.payload.type,
          isCorrect: false,
        },
        score: state.score - 50,
        [action.payload.type === "origin"
          ? "originGuessDistance"
          : "destinationGuessDistance"]: distance,
        [action.payload.type === "origin" ? "originState" : "destinationState"]:
          "error",
        airportStates: {
          ...state.airportStates,
          [guessedAirport?.iata || ""]: {
            state: stateForDistance(distance),
            distance: distance,
          },
        },
        [action.payload.type === "origin" ? "originGuess" : "destinationGuess"]:
          action.payload.airport_guess,
        incorrectGuessCount: state.incorrectGuessCount + 1,
      };
    case RoundActionType.GiveUp:
      return {
        ...state,
        score: 0,
      };
  }
  return state;
}
